import React from "react";

import _ from "underscore";
import isPlainObject from "lodash/isPlainObject";

import PropTypes from 'prop-types';

import MultiSelectFilter from './MultiSelectFilter';
import AddItemFromList from './AddItemFromList';
import Immutable from 'immutable';

const MAX_FILTER_COUNT = 150;

class MultiSelectFilterContainer extends React.Component {
  constructor(...args) {
    super(...args);

    this.addFilter = this.addFilter.bind(this);
    this.removeFilter = this.removeFilter.bind(this);
    this._applyFilter = this._applyFilter.bind(this);
    this._canFilter = this._canFilter.bind(this);

    const paramFilters = this.props.paramFilters;
    let filters = Immutable.OrderedMap();

    /**
     * ex: {
     *  advertiser: [2, 7],                                                      // single filter
     *  signal: { customer.boolean_2: [0], customer.boolean_4: ["null", 1] }     // filter collection
     * }
     * in tests
     * - 'advertiser' is a single filter that is also a param filter with values
     * - 'signal' is an collection of multiples param filters in object
     */
    _.each(this.props.filters, function(config, key) {
      if (this._containsFilterCollection(config)) {
        const itemFilters = config.items.reduce((map, { id }) => ({
          ...map,
          ...(paramFilters[id] && { [id]: paramFilters[id] })
        }), {});

        if (!_.isEmpty(itemFilters)) {
          filters = filters.set(key, itemFilters);
        }
      } else if (paramFilters[key]) {
        filters = filters.set(key, paramFilters[key]);
      }
    }, this);

    this.state = {
      filters: filters
    };
  }

  _containsFilterCollection(filterConfig) {
    return !!filterConfig.items;
  }

  _isFilterCollection(filter) {
    return isPlainObject(filter);
  }

  _initialValueForFilter(key) {
    const emptyFilterCollection = {};
    const emptyFilterValuesForOneFilter = [];

    return this._containsFilterCollection(this.props.filters[key])
      ? emptyFilterCollection
      : emptyFilterValuesForOneFilter;
  }

  addFilter(filter) {
    this.setState({
      filters: this.state.filters.set(filter.key, this._initialValueForFilter(filter.key)),
      openFilter: filter.key
    });
  }

  removeFilter(filterName) {
    this.updateFilters(this.state.filters.delete(filterName));
  }

  _applyFilter(filters, name) {
    let value;
    if (this._isFilterCollection(filters)) {
      value = filters;
    } else {
      value = _.pluck(filters, 'id');
    }
    this.updateFilters(this.state.filters.set(name, value));
  }

  updateFilters(filters) {
    this.setState({
      filters: filters
    });

    this.props.callback(this._filtersToObject(filters));
  }

  _filtersToObject(filters) {
    const filterConfigs = this.props.filters;
    return filters.reduce((obj, value, key) => {
      if (this._containsFilterCollection(filterConfigs[key])) {
        return { ...obj, ...value };
      } else {
        return { ...obj, [key]: value };
      }
    }, {});
  }

  destroyFilters() {
    if (this.state.filters.size > 0) {
      var filters = Immutable.OrderedMap();
      this.updateFilters(filters);
    }
  }

  render() {
    const {
      filters,
      titlePrefix,
      hideLabels,
      fixedWidth,
      shouldSort,
      enabled,
      includeUrlParams,
      customFilters
    } = this.props;

    var multiSelectFilters = this.state.filters.map(function(paramFilters, filterName) {
      const filterConfig = filters[filterName];
      const values = this._containsFilterCollection(filterConfig) ? { items: filterConfig.items } : filterConfig.values;

      return (
        <MultiSelectFilter
          customSelection={filterConfig.customSelection}
          name={filterName}
          title={titlePrefix + filterConfig.friendlyName}
          hideLabel={hideLabels}
          allSelectedText={filterConfig.allSelectedText}
          fixedWidth={fixedWidth}
          url={filterConfig.url}
          values={values}
          onApply={this._applyFilter}
          initialItems={paramFilters}
          key={filterName}
          canFilter={this._canFilter}
          maxItems={MAX_FILTER_COUNT}
          open={this._isFilterOpen(filterName)}
          onRemoveFilter={this.removeFilter}
          includeUrlParams={filterConfig.includeUrlParams || includeUrlParams}
        />
      );
    }, this);

    var addFilterDropDown =
      <AddItemFromList
        availableItems={ this._getNonActiveFilters() }
        itemCallback={ this.addFilter }
        label={ this._getAddFilterLabel() }
        shouldSort={shouldSort}
        disabled={!enabled}
        disabledButtonLabel={ "You have selected all of your filters" }
      />;

    return (
      <div data-id="multi-select-filter-container" className="multiselect-dropdown-container-group">
        {multiSelectFilters.valueSeq()}
        {customFilters}
        {addFilterDropDown}
      </div>
    );
  }

  _getAddFilterLabel() {
    if (this.state.filters.isEmpty() && !this.props.hideLabels) {
      return "Add Filter";
    } else {
      return "";
    }
  }

  _getNonActiveFilters() {
    var nonActiveFilters = Immutable.OrderedMap(this.props.filters).filterNot(function(config, filterName) {
      return this.state.filters.has(filterName);
    }, this).toObject();

    var availableFilters = Object.keys(nonActiveFilters).map(function(key) {
      var filterConfig = nonActiveFilters[key];
      filterConfig["key"] = key;
      return filterConfig;
    });

    return availableFilters;
  }

  _isFilterOpen(filterName) {
    return filterName === this.state.openFilter;
  }

  _canFilter(filters, filterName) {
    var count = 0;
    this.state.filters.forEach(function(filter, key) {
      if (key !== filterName) {
        count += this._isFilterCollection(filter) ? Object.keys(filter).length : filter.length;
      }
    }, this);
    return (count + filters.length) <= MAX_FILTER_COUNT;
  }
}

MultiSelectFilterContainer.propTypes = {
  callback:         PropTypes.func.isRequired,
  filters:          PropTypes.object,
  paramFilters:     PropTypes.object,
  titlePrefix:      PropTypes.string,
  hideLabels:       PropTypes.bool,
  enabled:          PropTypes.bool,
  fixedWidth:       PropTypes.bool,
  shouldSort:       PropTypes.bool,
  includeUrlParams: PropTypes.arrayOf(PropTypes.string),
  customFilters:    PropTypes.node
};

MultiSelectFilterContainer.defaultProps = {
  paramFilters: {}
};

export default MultiSelectFilterContainer;
